/**
 * GateBScreen — Quality Gate B por execução (F3-FE-2).
 *
 * Porta o GateShell do protótipo para o Gate B real, lendo:
 *   GET /executions/{id}/gates/b
 *
 * e enviando:
 *   PATCH /executions/{id}/gates/b  { decision: approve|reject|clarify, comment? }
 *   comment é obrigatório em reject/clarify.
 *
 * Layout fiel ao protótipo (mesmo padrão do GateAScreen):
 *   Header     — shield, título, SLA, badge de status, aprovadores
 *   Pills      — estimativa de tokens/custo, total de tasks, tasks pendentes
 *   Plan Tasks — lista de tasks com prioridade, pontos estimados e status
 *   Ações      — Aprovar / Rejeitar / Esclarecer + campo de comentário
 *
 * Segurança:
 *   - 403 → ForbiddenBanner
 *   - comment validado no client antes de enviar (reject/clarify requerem comentário)
 *   - Nunca exibe wallet_txn_id
 *
 * Acessibilidade:
 *   - Accordions com aria-expanded / aria-controls
 *   - Botões de decisão com aria-label descritivo
 *   - prefers-reduced-motion respeita animações
 *
 * Standards: Clean, NFR (a11y), OWASP.
 */

import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ShieldCheck, ChevronDown, ChevronRight as ChevronRightIcon,
  CheckCircle, XCircle, HelpCircle, Clock, Users,
  AlertCircle, List, Zap, ArrowLeft,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge, ForbiddenBanner } from '@/components/ui';
import { LoadingSpinner, QueryError, MutationError } from '@/components/ui/QueryState';
import { useExecutionGateB, useDecideGateB, useEditarRoteiro } from '@/api/executions';
import {
  ContratoDeFabricacao, bloqueiosAbertos, JUSTIFICATIVA_MINIMA,
} from './ContratoDeFabricacao';
import { useNavigationContext } from '@/navigation';
import { EstimativaPorAgente } from '@/components/agentes/EstimativaPorAgente';

// ─── Constantes ───────────────────────────────────────────────────────────────

const GATE_STATUS_META = {
  pending:   { label: 'AGUARDANDO APROVAÇÃO',       color: C.digital, pulse: true  },
  approved:  { label: 'APROVADO',                   color: C.cyber,   pulse: false },
  rejected:  { label: 'REJEITADO',                  color: C.hot,     pulse: false },
  clarify:   { label: 'ESCLARECIMENTO SOLICITADO',  color: C.neon,    pulse: false },
};

const SLA_LABEL = {
  standard:  '24h',
  expedited: '12h',
  critical:  '4h',
};

/** Prioridade da task → cor */
const PRIORITY_COLOR = {
  high:   C.hot,
  medium: C.neon,
  low:    C.cyber,
};

const PRIORITY_LABEL = {
  high:   'ALTA',
  medium: 'MÉDIA',
  low:    'BAIXA',
};

const TASK_STATUS_META = {
  pending:     { label: 'PENDENTE',      color: C.neutralMuted },
  in_progress: { label: 'EM ANDAMENTO',  color: C.cyber        },
  done:        { label: 'CONCLUÍDO',     color: C.cyberDim     },
  blocked:     { label: 'BLOQUEADO',     color: C.hot          },
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function MetaPill({ label, value, color = C.neutralDim }) {
  return (
    <div style={{
      display:         'flex',
      flexDirection:   'column',
      gap:             2,
      padding:         '10px 14px',
      backgroundColor: C.blackElev2,
      borderRadius:    8,
      border:          `1px solid ${C.blackBorder}`,
      minWidth:        90,
    }}>
      <span style={{ fontSize: 18, fontWeight: 700, color: color ?? C.neutral }}>
        {value}
      </span>
      <span style={{ fontSize: 10, color: C.neutralMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </span>
    </div>
  );
}

/** Item de task do plano */
function PlanTaskItem({ task, index }) {
  const [open, setOpen] = useState(false);
  const priorityColor   = PRIORITY_COLOR[task.priority] ?? C.neutralDim;
  const statusMeta      = TASK_STATUS_META[task.status] ?? TASK_STATUS_META.pending;
  const accordionId     = `task-btn-${task.task_id}`;
  const panelId         = `task-panel-${task.task_id}`;

  return (
    <div style={{
      border:       `1px solid ${C.blackBorder}`,
      borderRadius: 8,
      overflow:     'hidden',
      marginBottom: 6,
    }}>
      <button
        id={accordionId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((p) => !p)}
        style={{
          width:           '100%',
          display:         'flex',
          alignItems:      'center',
          gap:             12,
          padding:         '10px 14px',
          backgroundColor: open ? C.blackElev2 : C.blackElev,
          border:          'none',
          cursor:          'pointer',
          textAlign:       'left',
          transition:      'background-color 150ms',
        }}
      >
        <span style={{
          fontSize:   11,
          fontWeight: 700,
          color:      C.neutralMuted,
          flexShrink: 0,
          width:      30,
        }}>
          #{String(index + 1).padStart(2, '0')}
        </span>

        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.neutral }}>
          {task.title}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {/* Prioridade */}
          <span style={{
            fontSize:        10,
            fontWeight:      700,
            padding:         '2px 7px',
            borderRadius:    4,
            backgroundColor: `${priorityColor}22`,
            border:          `1px solid ${priorityColor}55`,
            color:           priorityColor,
            textTransform:   'uppercase',
            letterSpacing:   '0.04em',
          }}>
            {PRIORITY_LABEL[task.priority] ?? task.priority ?? '—'}
          </span>

          {/* Pontos */}
          {task.estimate_points != null && (
            <span style={{
              fontSize:        10,
              fontWeight:      700,
              padding:         '2px 7px',
              borderRadius:    4,
              backgroundColor: `${C.quantum}22`,
              border:          `1px solid ${C.quantum}44`,
              color:           C.quantum,
            }}>
              {task.estimate_points} pts
            </span>
          )}

          {/* Status */}
          <span style={{
            fontSize:        10,
            fontWeight:      700,
            color:           statusMeta.color,
            letterSpacing:   '0.04em',
            textTransform:   'uppercase',
          }}>
            {statusMeta.label}
          </span>

          {open
            ? <ChevronDown size={13} aria-hidden="true" style={{ color: C.neutralMuted }} />
            : <ChevronRightIcon size={13} aria-hidden="true" style={{ color: C.neutralMuted }} />}
        </div>
      </button>

      {open && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={accordionId}
          style={{ padding: '10px 14px', backgroundColor: C.blackElev2 }}
        >
          <div style={{ fontSize: 11, color: C.neutralMuted, fontFamily: 'monospace' }}>
            task_id: {task.task_id}
          </div>
        </div>
      )}
    </div>
  );
}

/** Painel de ações de decisão — Aprovar / Rejeitar / Esclarecer */
function DecisionPanel({
  executionId, gateStatus, onSuccess, onBackToBoard, contract, contractMode, aceites,
}) {
  const [comment, setComment]     = useState('');
  const [decision, setDecision]   = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [validationErr, setValidationErr] = useState('');

  const { mutate, isPending, error, reset } = useDecideGateB(executionId);

  const handleDecide = useCallback((d) => {
    if (isPending) return;

    // comment obrigatório em reject/clarify
    if ((d === 'reject' || d === 'clarify') && !comment.trim()) {
      setValidationErr('Comentário obrigatório para rejeitar ou solicitar esclarecimento.');
      return;
    }
    // Contrato (Onda 2): aceite de bloqueio só vai com approve. No modo estrito, cada
    // bloqueio aberto precisa de justificativa — a API recusa (409) sem ela.
    if (d === 'approve' && contractMode === 'strict' && !contract) {
      setValidationErr(
        'Modo estrito sem contrato de fabricação: peça esclarecimento para remontá-lo '
        + '(ou rejeite).',
      );
      return;
    }
    const abertos = bloqueiosAbertos(contract);
    const justificados = abertos.filter(
      (g) => (aceites?.[g.id] ?? '').trim().length >= JUSTIFICATIVA_MINIMA,
    );
    if (d === 'approve' && contractMode === 'strict' && justificados.length < abertos.length) {
      setValidationErr(
        `Modo estrito: justifique cada bloqueio do contrato (mínimo ${JUSTIFICATIVA_MINIMA} `
        + 'caracteres) ou peça esclarecimento.',
      );
      return;
    }
    setValidationErr('');
    setDecision(d);

    const acceptedGaps = d === 'approve'
      ? justificados.map((g) => ({ gap_id: g.id, justification: aceites[g.id].trim() }))
      : [];
    mutate(
      {
        decision: d,
        comment: comment.trim() || undefined,
        ...(acceptedGaps.length ? { accepted_gaps: acceptedGaps } : {}),
      },
      {
        onSuccess: () => {
          setSubmitted(true);
          if (onSuccess) onSuccess(d);
        },
      },
    );
  }, [isPending, mutate, comment, onSuccess, contract, contractMode, aceites]);

  if (submitted) {
    const labels = { approve: 'aprovado', reject: 'rejeitado', clarify: 'esclarecimento solicitado' };
    return (
      <Card style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <CheckCircle size={20} aria-hidden="true" style={{ color: C.cyber }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.cyber }}>
              Gate B {labels[decision] ?? 'respondido'}
            </div>
            <div style={{ fontSize: 12, color: C.neutralDim, marginTop: 2 }}>
              Decisão registrada com sucesso.
            </div>
          </div>
          {onBackToBoard && (
            <Btn variant="primary" size="sm" icon={ArrowLeft} onClick={onBackToBoard}>
              Voltar ao Live Board
            </Btn>
          )}
        </div>
      </Card>
    );
  }

  const alreadyDecided = gateStatus && gateStatus !== 'pending';
  if (alreadyDecided) {
    return onBackToBoard ? (
      <Card style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: C.neutralDim, flex: 1, minWidth: 200 }}>
            Este gate já foi decidido. Volte ao Live Board para acompanhar a execução.
          </span>
          <Btn variant="primary" size="sm" icon={ArrowLeft} onClick={onBackToBoard}>
            Voltar ao Live Board
          </Btn>
        </div>
      </Card>
    ) : null;
  }

  return (
    <Card style={{ padding: 20 }}>
      <div style={{
        fontSize:      11,
        fontWeight:    700,
        color:         C.neutralMuted,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        marginBottom:  14,
      }}>
        Decisão
      </div>

      {/* Campo de comentário */}
      <div style={{ marginBottom: 16 }}>
        <label
          htmlFor="gateb-comment"
          style={{ fontSize: 12, color: C.neutralDim, display: 'block', marginBottom: 6 }}
        >
          Parecer / Comentário{' '}
          <span style={{ color: C.hot }}>
            (obrigatório ao rejeitar ou solicitar esclarecimento)
          </span>
        </label>
        <textarea
          id="gateb-comment"
          value={comment}
          onChange={(e) => { setComment(e.target.value); setValidationErr(''); reset(); }}
          rows={3}
          placeholder="Adicione um comentário para o registro de auditoria..."
          style={{
            width:           '100%',
            backgroundColor: C.blackElev2,
            border:          `1px solid ${validationErr ? C.hot : C.blackBorder}`,
            borderRadius:    6,
            color:           C.neutral,
            fontSize:        13,
            padding:         '10px 12px',
            resize:          'vertical',
            fontFamily:      'inherit',
            outline:         'none',
            boxSizing:       'border-box',
          }}
          aria-describedby={validationErr ? 'gateb-comment-err' : undefined}
        />
        {validationErr && (
          <div
            id="gateb-comment-err"
            role="alert"
            style={{ fontSize: 11, color: C.hot, marginTop: 4 }}
          >
            {validationErr}
          </div>
        )}
      </div>

      {error && <MutationError error={error} onDismiss={reset} />}

      {/* Botões */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Btn
          variant="primary"
          icon={CheckCircle}
          disabled={isPending}
          onClick={() => handleDecide('approve')}
          aria-label="Aprovar Gate B"
        >
          {isPending && decision === 'approve' ? 'Aprovando...' : 'Aprovar'}
        </Btn>

        <Btn
          variant="outline"
          icon={HelpCircle}
          disabled={isPending}
          onClick={() => handleDecide('clarify')}
          aria-label="Solicitar esclarecimento no Gate B"
        >
          {isPending && decision === 'clarify' ? 'Enviando...' : 'Pedir Esclarecimento'}
        </Btn>

        <Btn
          variant="danger"
          icon={XCircle}
          disabled={isPending}
          onClick={() => handleDecide('reject')}
          aria-label="Rejeitar Gate B"
        >
          {isPending && decision === 'reject' ? 'Rejeitando...' : 'Rejeitar'}
        </Btn>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: C.neutralMuted, lineHeight: 1.5 }}>
        Toda decisão é registrada de forma imutável na trilha de auditoria com IP, timestamp e usuário.
      </div>
    </Card>
  );
}

// ─── Tela principal ───────────────────────────────────────────────────────────

/**
 * GateBScreen — Gate B para uma execução específica.
 * Lê executionId de ?execution=<id>.
 */
export function GateBScreen() {
  const [searchParams] = useSearchParams();
  const executionId = searchParams.get('execution');
  const { navigate } = useNavigationContext();

  const {
    data: gate,
    isLoading,
    error,
    refetch,
  } = useExecutionGateB(executionId);
  // Justificativas de aceite por bloqueio do contrato ({gap_id: texto}) — Onda 2.
  const [aceites, setAceites] = useState({});
  // Versão nova do contrato (clarify) = lacunas novas: justificativa antiga não vale.
  const versaoDoContrato = gate?.contract?.version;
  useEffect(() => { setAceites({}); }, [versaoDoContrato]);
  // Roteiro de validação (projeto de agente): o revisor ajusta os casos antes de aprovar.
  const roteiro = useEditarRoteiro(executionId);
  const errosDoRoteiro = roteiro.error
    ? (roteiro.error.problemBody?.errors ?? roteiro.error.problemBody?.detail?.errors
      ?? [roteiro.error.detail || roteiro.error.title || 'Não foi possível salvar o roteiro.'])
    : [];

  if (!executionId) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ fontSize: 14, color: C.neutralMuted }}>
          Nenhuma execução selecionada. Abra o Gate B a partir da fila de gates.
        </div>
        <div style={{ marginTop: 12 }}>
          <Btn variant="ghost" size="sm" onClick={() => navigate('gates')}>
            Ver fila de gates
          </Btn>
        </div>
      </div>
    );
  }

  if (isLoading) return <LoadingSpinner label="Carregando Gate B..." />;

  if (error) {
    if (error.isForbidden) {
      return (
        <div style={{ padding: 24 }}>
          <ForbiddenBanner detail={error.detail} />
        </div>
      );
    }
    return (
      <div style={{ padding: 24 }}>
        <QueryError error={error} onRetry={refetch} />
      </div>
    );
  }

  if (gate === null) {
    return (
      <div style={{
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        gap:           12,
        padding:       48,
      }}>
        <Clock size={40} aria-hidden="true" style={{ color: C.blackBorder }} />
        <div style={{ fontSize: 15, fontWeight: 600, color: C.neutralMuted }}>
          Gate B ainda não criado
        </div>
        <div style={{ fontSize: 13, color: C.neutralMuted, textAlign: 'center', maxWidth: 360 }}>
          Este gate será criado ao concluir o planejamento do ciclo.
        </div>
        <Btn variant="ghost" size="sm" onClick={() => navigate('gates')}>
          Ver fila de gates
        </Btn>
      </div>
    );
  }

  // ── Derivar métricas ──────────────────────────────────────────────────────
  // Escopo 'analise': sem backlog — o Gate B aprova a ANÁLISE, não um plano de
  // tasks. Esconde pills/seção de tasks e mostra a revisão da análise.
  const isAnalise   = gate.delivery_scope === 'analise';
  const tasks       = gate.plan_tasks ?? [];
  const totalTasks  = tasks.length;
  const doneTasks   = tasks.filter((t) => t.status === 'done').length;
  const pendingTasks= tasks.filter((t) => t.status === 'pending').length;
  const totalPoints = tasks.reduce((acc, t) => acc + (t.estimate_points ?? 0), 0);

  const statusMeta  = GATE_STATUS_META[gate.status] ?? GATE_STATUS_META.pending;
  const slaLabel    = SLA_LABEL[gate.sla_tier] ?? gate.sla_tier ?? '24h';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{
        display:      'flex',
        alignItems:   'flex-start',
        gap:          16,
        marginBottom: 28,
      }}>
        <div style={{
          width:           44,
          height:          44,
          borderRadius:    10,
          backgroundColor: `${C.quantum}18`,
          border:          `1px solid ${C.quantum}44`,
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          flexShrink:      0,
        }}>
          <ShieldCheck size={22} aria-hidden="true" style={{ color: C.quantum }} />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: C.neutral, margin: 0 }}>
              Gate B — Plano de Execução
            </h1>
            <span style={{
              fontSize:        10,
              fontWeight:      700,
              padding:         '3px 8px',
              borderRadius:    4,
              backgroundColor: `${statusMeta.color}22`,
              border:          `1px solid ${statusMeta.color}55`,
              color:           statusMeta.color,
              letterSpacing:   '0.04em',
              textTransform:   'uppercase',
              animation:       statusMeta.pulse ? 'navaPulse 2s ease-out infinite' : 'none',
            }}>
              {statusMeta.label}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: C.neutralMuted }}>
              <Clock size={11} aria-hidden="true" style={{ display: 'inline', marginRight: 4 }} />
              SLA: {slaLabel}
            </span>

            {gate.approvers && gate.approvers.length > 0 && (
              <span style={{ fontSize: 12, color: C.neutralMuted }}>
                <Users size={11} aria-hidden="true" style={{ display: 'inline', marginRight: 4 }} />
                {gate.approvers.join(' · ')}
              </span>
            )}

            {gate.decided_at && (
              <span style={{ fontSize: 12, color: C.neutralDim }}>
                Decidido em: {new Date(gate.decided_at).toLocaleString('pt-BR')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Pills de métricas ───────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
        {!isAnalise && (
          <>
            <MetaPill label="Tasks"         value={totalTasks}   color={C.neutral}  />
            <MetaPill label="Pendentes"     value={pendingTasks} color={C.digital}  />
            <MetaPill label="Concluídas"    value={doneTasks}    color={C.cyber}    />
            <MetaPill label="Story Points"  value={totalPoints}  color={C.quantum}  />
          </>
        )}
        {gate.estimate_tokens != null && (
          <MetaPill
            label="Est. Tokens"
            value={Number(gate.estimate_tokens).toLocaleString('pt-BR')}
            color={C.cyber}
          />
        )}
        {gate.estimate_cost_usd != null && (
          <MetaPill
            label="Est. Custo"
            value={`US$ ${Number(gate.estimate_cost_usd).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            color={C.neon}
          />
        )}
      </div>

      {/* ── Estimativa por agente (A3 FR-22) ────────────────────────────── */}
      <EstimativaPorAgente
        itens={gate.estimativa_por_agente ?? gate.gate_b?.estimativa_por_agente}
      />

      {/* ── Comentário do gate (se houver) ──────────────────────────────── */}
      {gate.comment && (
        <div style={{
          display:         'flex',
          gap:             10,
          padding:         '12px 16px',
          borderRadius:    8,
          backgroundColor: `${C.quantum}11`,
          border:          `1px solid ${C.quantum}33`,
          marginBottom:    20,
        }}>
          <Zap size={15} aria-hidden="true" style={{ color: C.quantum, flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.quantum, marginBottom: 2 }}>
              Comentário do gate
            </div>
            <div style={{ fontSize: 12, color: C.neutralDim }}>
              {gate.comment}
            </div>
          </div>
        </div>
      )}

      {/* ── Contrato de fabricação (Onda 2) ─────────────────────────────── */}
      <ContratoDeFabricacao
        contract={gate.contract ?? null}
        contractMode={gate.contract_mode ?? 'hybrid'}
        aceites={aceites}
        onAceite={(id, texto) => setAceites((a) => ({ ...a, [id]: texto }))}
        editavel={!gate.status || gate.status === 'pending'}
        onSalvarRoteiro={(casos) => roteiro.mutate(casos)}
        salvandoRoteiro={roteiro.isPending}
        errosDoRoteiro={errosDoRoteiro}
      />

      {/* ── Plano de Tasks (oculto em 'analise' — sem backlog) ──────────── */}
      {isAnalise ? (
        <section aria-labelledby="plan-tasks-heading" style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <List size={14} aria-hidden="true" style={{ color: C.quantum }} />
            <h2
              id="plan-tasks-heading"
              style={{
                fontSize:      12,
                fontWeight:    700,
                color:         C.neutralMuted,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                margin:        0,
              }}
            >
              Revisão da Análise
            </h2>
          </div>
          <div style={{
            padding:       '20px 24px',
            fontSize:      13,
            lineHeight:    1.6,
            color:         C.neutralDim,
            border:        `1px dashed ${C.blackBorder}`,
            borderRadius:  8,
          }}>
            Projeto de <strong style={{ color: C.neutral }}>Análise</strong> — não há backlog nem
            geração de código. Aprovar aqui valida a análise entregue; os artefatos
            (diagnóstico, recomendações, diagramas e tabelas) ficam em{' '}
            <strong style={{ color: C.neutral }}>Saída / Análise</strong> da execução.
          </div>
        </section>
      ) : (
        <section aria-labelledby="plan-tasks-heading" style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <List size={14} aria-hidden="true" style={{ color: C.quantum }} />
            <h2
              id="plan-tasks-heading"
              style={{
                fontSize:      12,
                fontWeight:    700,
                color:         C.neutralMuted,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                margin:        0,
              }}
            >
              Plano de Tasks ({totalTasks})
            </h2>
          </div>

          {tasks.length === 0
            ? (
              <div style={{
                padding:       32,
                textAlign:     'center',
                fontSize:      13,
                color:         C.neutralMuted,
                border:        `1px dashed ${C.blackBorder}`,
                borderRadius:  8,
              }}>
                Nenhuma task registrada neste gate.
              </div>
            )
            : tasks.map((task, i) => (
              <PlanTaskItem key={task.task_id} task={task} index={i} />
            ))}
        </section>
      )}

      {/* ── Painel de decisão ────────────────────────────────────────────── */}
      <DecisionPanel
        executionId={executionId}
        gateStatus={gate.status}
        onBackToBoard={() => navigate(`/execution/liveboard?execution=${executionId}`)}
        onSuccess={() => {}}
        contract={gate.contract ?? null}
        contractMode={gate.contract_mode ?? 'hybrid'}
        aceites={aceites}
      />
    </div>
  );
}
